import { apiClient } from './client';
import type { BaseResponse } from '@/types/api';

// ── Historical ──

export interface HistoricalRequest {
  station: string;
  date: string;
  soc_initial_pct?: number;
}

export interface EpochDetail {
  slot: number;
  time: string;
  actual_ep: number;
  optimal_ep: number;
  diff: number;
}

export interface HistoricalResult {
  station: string;
  date: string;
  pdiff: number;
  bat_capacity: number;
  price_rt: number[];
  pv_avail: number[];
  agc_target: number[];
  agc_target_opt: number[];
  actual: {
    ep: number[];
    revenue: number;
    penalty: number;
    net_revenue: number;
    soc_final: number;
    hourly_revenue: number[];
    pv_direct: number;
    bat_discharge: number;
    bat_charge: number;
    curtailment: number;
  };
  optimal: {
    ep: number[];
    revenue: number;
    penalty: number;
    net_revenue: number;
    soc_final: number;
    hourly_revenue: number[];
    pv_direct: number;
    bat_discharge: number;
    bat_charge: number;
    curtailment: number;
  };
  improvement: number;
  solve_time: number;
  status: string;
  top_diffs: EpochDetail[];
  all_diffs: EpochDetail[];
}

export async function runHistorical(req: HistoricalRequest): Promise<HistoricalResult> {
  const res = await apiClient.post<BaseResponse<HistoricalResult>>('/optimization/historical', req, { timeout: 60000 });
  return res.data.data;
}

// ── Forecast ──

export interface ForecastRequest {
  station: string;
  pv_forecast: number[];
  curtailment_rate?: number[];
  target_date?: string;
}

export interface ScenarioResult {
  name: string;
  net_revenue: number;
  penalty: number;
  soc_final: number;
}

export interface ForecastResult {
  station: string;
  target_date: string;
  pdiff: number;
  pv_forecast: number[];
  price_forecast: number[];
  curtailment_rate: number[];
  ep: number[];
  revenue: number;
  penalty: number;
  net_revenue: number;
  soc_final: number;
  total_grid: number;
  total_charge: number;
  total_discharge: number;
  total_curt: number;
  solve_time: number;
  status: string;
  scenarios: ScenarioResult[];
  risk_notes: string[];
}

export async function runForecast(req: ForecastRequest): Promise<ForecastResult> {
  const res = await apiClient.post<BaseResponse<ForecastResult>>('/optimization/forecast', req, { timeout: 300000 });
  return res.data.data;
}

// ── Curtailment Model ──

export interface CurtailmentModelState {
  station: string;
  method: string;
  train_days: number;
  last_train_date: string;
  daily_avg_rate: number;
  slot_rates: Record<string, number>;
}

export async function fetchCurtailmentModel(station: string): Promise<CurtailmentModelState> {
  const res = await apiClient.get<BaseResponse<CurtailmentModelState>>(`/optimization/curtailment-model/${encodeURIComponent(station)}`);
  return res.data.data;
}

// ── Strategy Guide ──

export interface StrategyWindow {
  type: 'charge' | 'discharge';
  start: string;
  end: string;
  avg_price: number;
}

export interface StrategyGuideResponse {
  station: string;
  target_date: string;
  mode: 'historical' | 'forecast';
  bat_capacity: number;
  pdiff: number;
  pv_forecast: number[];
  curtailment_rate: number[];
  agc_target_pred: number[];
  price_forecast: number[];
  price_upper: number[] | null;
  price_lower: number[] | null;
  optimal_ep: number[];
  expected: {
    net_revenue: number;
    bat_charge: number;
    bat_discharge: number;
    curtailment: number;
  };
  yesterday_compare: {
    net_revenue: number;
    bat_charge: number;
    bat_discharge: number;
    curtailment: number;
  } | null;
  report: {
    summary: string;
    windows: StrategyWindow[];
  } | null;
}

export async function fetchStrategyGuide(station: string, targetDate: string): Promise<StrategyGuideResponse> {
  const res = await apiClient.get<BaseResponse<StrategyGuideResponse>>(
    '/optimization/strategy-guide',
    { params: { station, target_date: targetDate } }
  );
  return res.data.data;
}
